<template>
  <div>
    <div class="container-fluid">
      <Breadcrumbs main="Розыгрыши" title="Купоны и победители"/>

      <div v-if="isLoading" class="loading-wrapper">
        <b-spinner class="spinner"></b-spinner>
      </div>

      <template v-else>
        <b-card class="mb-4">
          <div class="d-flex flex-wrap justify-content-between align-items-start">
            <div>
              <h5 class="mb-2">{{ giveaway && giveaway.name }}</h5>
              <b-badge :variant="getGiveawayStatusVariant(giveaway)">{{ getGiveawayStatusText(giveaway) }}</b-badge>
              <div class="mt-3 text-muted">
                <div>Дата старта: {{ formatGiveawayDate(giveaway && giveaway.starts_at) }}</div>
                <div>Дата окончания: {{ formatGiveawayDate(giveaway && giveaway.ends_at) }}</div>
              </div>
            </div>
            <div class="text-md-right mt-3 mt-md-0">
              <div class="mb-2">
                {{ getCouponsActivated(giveaway) }} / {{ getCouponsTotal(giveaway) }} активировано
              </div>
              <b-progress
                  :value="getCouponsProgress(giveaway)"
                  :max="100"
                  height="4px"
                  variant="primary"
                  class="progress-wide"
              ></b-progress>
              <b-button
                  variant="primary"
                  class="mt-3"
                  :to="{ name: 'edit_giveaway', params: { id: giveawayId } }"
              >
                Редактировать
              </b-button>
            </div>
          </div>
        </b-card>

        <b-card>
          <b-tabs>
            <b-tab title="Купоны" active>
              <div v-if="isCouponsLoading" class="loading-wrapper">
                <b-spinner class="spinner"></b-spinner>
              </div>
              <template v-else>
                <b-table
                    responsive
                    show-empty
                    stacked="md"
                    striped
                    bordered
                    :items="coupons"
                    :fields="couponFields"
                    :current-page="0"
                    class="mt-3 mb-4 table-no-scroll"
                    empty-text="Купоны отсутствуют"
                >
                  <template #cell(code)="data">
                    <span class="coupon-code">{{ data.item.code }}</span>
                  </template>

                  <template #cell(status)="data">
                    <b-badge :variant="data.item.status === 'activated' ? 'success' : 'secondary'">
                      {{ data.item.status === 'activated' ? 'Активирован' : 'Доступен' }}
                    </b-badge>
                  </template>

                  <template #cell(activated_by)="data">
                    {{ getUserName(data.item.activated_by) }}
                  </template>

                  <template #cell(activated_at)="data">
                    {{ data.item.activated_at ? formatGiveawayDate(data.item.activated_at) : '—' }}
                  </template>
                </b-table>

                <b-pagination
                    @change="handleCouponsPageChange"
                    v-model="couponsPage"
                    :total-rows="couponsPagination.total"
                    :per-page="couponsPagination.perPage"
                    class="my-0"
                ></b-pagination>
              </template>
            </b-tab>

            <b-tab title="Победители" @click="loadWinnersOnce">
              <div v-if="isWinnersLoading" class="loading-wrapper">
                <b-spinner class="spinner"></b-spinner>
              </div>
              <template v-else>
                <b-table
                    responsive
                    show-empty
                    stacked="md"
                    striped
                    bordered
                    :items="winners"
                    :fields="winnerFields"
                    :current-page="0"
                    class="mt-3 mb-4"
                    empty-text="Победителей пока нет"
                >
                  <template #cell(user)="data">
                    {{ getUserName(data.item.user) }}
                  </template>

                  <template #cell(game_ids)="data">
                    <div>MLBB ID: {{ data.item.mlbb_id || '—' }}</div>
                    <div>Zone ID: {{ data.item.zone_id || '—' }}</div>
                  </template>

                  <template #cell(contact)="data">
                    <template v-if="data.item.contact_value">
                      <div class="text-muted">{{ getContactProviderText(data.item.contact_provider) }}</div>
                      <a
                          v-if="data.item.contact_provider === 'telegram'"
                          :href="`https://t.me/${data.item.contact_value}`"
                          target="_blank"
                          rel="noopener noreferrer"
                      >{{ data.item.contact_value }}</a>
                      <span v-else>{{ data.item.contact_value }}</span>
                    </template>
                    <span v-else>—</span>
                  </template>

                  <template #cell(coupon)="data">
                    <div class="coupon-code">{{ data.item.coupon && data.item.coupon.code }}</div>
                    <div class="text-muted">попыток: {{ data.item.coupon && data.item.coupon.attempts_count }}</div>
                  </template>

                  <template #cell(activated_at)="data">
                    {{ formatGiveawayDate(data.item.activated_at) }}
                  </template>

                  <template #cell(reward_status)="data">
                    <b-badge :variant="getRewardStatusVariant(data.item.reward_status)" class="mb-2">
                      {{ getRewardStatusText(data.item.reward_status) }}
                    </b-badge>
                    <b-form-select
                        :value="data.item.reward_status"
                        :options="rewardStatusOptions"
                        :disabled="savingWinnerId === data.item.id"
                        size="sm"
                        @change="changeRewardStatus(data.item, $event)"
                    ></b-form-select>
                  </template>
                </b-table>

                <b-pagination
                    @change="handleWinnersPageChange"
                    v-model="winnersPage"
                    :total-rows="winnersPagination.total"
                    :per-page="winnersPagination.perPage"
                    class="my-0"
                ></b-pagination>
              </template>
            </b-tab>
          </b-tabs>
        </b-card>
      </template>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import {GiveawayAdapterMixin} from "@/mixins/Giveaways/GiveawayAdapterMixin";

export default {
  name: 'GiveawayDetails',
  mixins: [GiveawayAdapterMixin],
  data() {
    return {
      isLoading: false,
      isCouponsLoading: false,
      isWinnersLoading: false,
      isWinnersLoaded: false,
      savingWinnerId: null,
      couponsPage: 1,
      winnersPage: 1,
      couponFields: [
        {key: 'id', label: '№', sortable: true},
        {key: 'code', label: 'Код купона', sortable: true},
        {key: 'status', label: 'Статус', sortable: false},
        {key: 'activated_by', label: 'Активировал', sortable: false},
        {key: 'activated_at', label: 'Дата активации', sortable: true},
        {key: 'attempts_count', label: 'Попыток', sortable: true},
      ],
      winnerFields: [
        {key: 'id', label: '№', sortable: true},
        {key: 'user', label: 'Пользователь', sortable: false},
        {key: 'game_ids', label: 'Игровой профиль', sortable: false},
        {key: 'contact', label: 'Контакт', sortable: false},
        {key: 'coupon', label: 'Купон', sortable: false},
        {key: 'activated_at', label: 'Дата активации', sortable: true},
        {key: 'reward_status', label: 'Выдача награды', sortable: false},
      ],
    };
  },
  computed: {
    ...mapGetters('moduleGiveaways', [
      'getCurrentGiveaway',
      'getGiveawayCoupons',
      'getGiveawayCouponsPagination',
      'getGiveawayWinners',
      'getGiveawayWinnersPagination',
    ]),
    giveawayId() {
      return this.$route.params.id;
    },
    giveaway() {
      return this.getCurrentGiveaway;
    },
    coupons() {
      return this.getGiveawayCoupons;
    },
    couponsPagination() {
      return this.getGiveawayCouponsPagination;
    },
    winners() {
      return this.getGiveawayWinners;
    },
    winnersPagination() {
      return this.getGiveawayWinnersPagination;
    },
  },
  mounted() {
    this.loadGiveaway();
    this.loadCoupons();
  },
  methods: {
    ...mapActions('moduleGiveaways', [
      'fetchGiveaway',
      'fetchGiveawayCoupons',
      'fetchGiveawayWinners',
      'updateWinnerRewardStatus',
    ]),
    getUserName(user) {
      if (!user) return '—';
      return user.username || user.email || `ID ${user.id}`;
    },
    async loadGiveaway() {
      this.isLoading = true;
      try {
        await this.fetchGiveaway(this.giveawayId);
      } catch (error) {
        console.error('Error loading giveaway:', error);
      } finally {
        this.isLoading = false;
      }
    },
    async loadCoupons() {
      this.isCouponsLoading = true;
      try {
        await this.fetchGiveawayCoupons({id: this.giveawayId, params: {page: this.couponsPage}});
      } catch (error) {
        console.error('Error loading coupons:', error);
      } finally {
        this.isCouponsLoading = false;
      }
    },
    async loadWinners() {
      this.isWinnersLoading = true;
      try {
        await this.fetchGiveawayWinners({id: this.giveawayId, params: {page: this.winnersPage}});
        this.isWinnersLoaded = true;
      } catch (error) {
        console.error('Error loading winners:', error);
      } finally {
        this.isWinnersLoading = false;
      }
    },
    loadWinnersOnce() {
      if (this.isWinnersLoaded) return;
      this.loadWinners();
    },
    handleCouponsPageChange(pageNumber) {
      this.couponsPage = pageNumber;
      this.loadCoupons();
    },
    handleWinnersPageChange(pageNumber) {
      this.winnersPage = pageNumber;
      this.loadWinners();
    },
    async changeRewardStatus(winner, rewardStatus) {
      if (!rewardStatus || rewardStatus === winner.reward_status) return;

      this.savingWinnerId = winner.id;
      try {
        await this.updateWinnerRewardStatus({
          id: this.giveawayId,
          winnerId: winner.id,
          rewardStatus,
        });
      } catch (error) {
        console.error('Error updating reward status:', error);
      } finally {
        this.savingWinnerId = null;
      }
    },
  }
};
</script>

<style scoped>
.loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
}

.progress-wide {
  min-width: 220px;
}

.coupon-code {
  font-family: monospace;
  letter-spacing: 1px;
}

.table-no-scroll {
  overflow-x: hidden !important;
}
</style>
